<template>
  <div
    class="d-flex-column justify-start align-start gap-20"
    style="margin-top: 12px"
  >
    <h4 class="t-white font-family-1 title-h5 f-w-600">
      {{ $t("calculators_common.customize") }}
    </h4>

    <div class="d-flex-column justify-start align-start exptra_options gap-24">
      <div class="justify-start picker_option_container flex-wrap">
        <div
          class="d-flex-column justify-start align-start gap-0"
          v-for="packOption in packOptions"
          :key="packOption"
        >
          <div
            class="picker_option d-flex-column space-between gap-16 align-start"
            :class="[
              isOptionSelected(packOption.id) && 'selected',
              isDisabled(packOption) && 'disabled',
            ]"
            @click.stop="() => selectOption(packOption)"
          >
            <div class="d-flex-row justify-start gap-5">
              <p class="t-white medium-l font-family-2">
                {{ packOption.title }}
              </p>
              <Tooltip
                :text="packOption.description"
                :position="'top'"
                v-if="!$isMobile()"
              >
                <poi-icon />
              </Tooltip>

              <h4 class="font-family-1 caps bold-s t-blue" v-if="$isMobile()">
                {{ renderPrice(packOption) }}
              </h4>
            </div>
            <div class="d-flex-row space-between" v-if="!$isMobile()">
              <h4 class="price_container font-family-1 caps bold-s t-blue">
                {{ renderPrice(packOption) }}
              </h4>
              <Switch
                :checked="isOptionSelected(packOption.id)"
                :click-events="switchClick"
                :click-param="packOption"
              />
            </div>
          </div>
          <Transition name="slide-fade-small">
            <p
              v-if="$isMobile() && isOptionSelected(packOption.id)"
              class="font-family-2 regular-m t-light-orange mobile_info_description"
            >
              {{
                packOption.title?.includes("new_text")
                  ? $t(`${packOption.title}_desc`)
                  : packOption.description
              }}
            </p>
          </Transition>
        </div>
      </div>

      <div
        class="d-flex-column justify-start orbs_amount_picker b-radius-9 align-start gap-16"
        v-if="divineOrbSelected"
      >
        <p class="t-white bold-l font-family-2">
          {{ $t("path_of_exile.orbs_amount") }}
        </p>
        <div class="picker_option_container justify-start flex-wrap">
          <button
            v-for="option in divineOrbsOptions"
            class="role_picker d-flex-row space-between gap-5 medium-l font-family-2"
            :key="option.id"
            :class="[isOptionSelected(option.id) && 'selected']"
            @click.stop="() => selectChildOption(option)"
          >
            <div class="d-flex-row justify-start">
              <img
                src="/images/games/path-of-exile-2-boost/currencies/divine_orb.webp"
                class="divine_orb_image"
              />
              <p class="t-white medium-l font-family-2">
                {{ option.title }}
              </p>
            </div>
            <h4 class="font-family-2 caps regular-s t-blue">
              {{ renderPrice(option) }}
            </h4>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import PoiIcon from "../../../icons/PoiIcon.vue";
import Switch from "../../../custom/Switch.vue";
import Tooltip from "../../../custom/Tooltip.vue";
import { calculateCurrency } from "~/calculate/calculatePrice";

const { getGame, getGamePack } = useGameStore();
const { getUserCurrency } = useUserStore();
const checkoutStore = useCheckoutStore();

const packOptions = computed(() => {
  const gamePackOptions =
    getGamePack.pack_options?.filter(
      (packOp) =>
        packOp?.const_title !== "self_play" &&
        packOp.slug === "" &&
        packOp.const_title !== "five_divine_orbs" &&
        packOp.const_title !== "fifteen_divine_orbs" &&
        packOp.const_title !== "thirty_divine_orbs"
    ) || [];
  let parsedGameOptions = [];
  gamePackOptions.forEach((option) => {
    if (option.rules.bonus) {
      let key = "";
      if (checkoutStore.getDetails.desiredRankTitle) {
        if (!checkoutStore.getDetails.desiredDivisionTitle) {
          key = checkoutStore.getDetails.desiredRankTitle;
        } else {
          key =
            checkoutStore.getDetails.desiredRankTitle +
            " " +
            checkoutStore.getDetails.desiredDivisionTitle;
        }
      } else {
        if (!checkoutStore.getDetails.currentDivisionTitle) {
          key = checkoutStore.getDetails.currentRankTitle;
        } else {
          key =
            checkoutStore.getDetails.currentRankTitle +
            " " +
            checkoutStore.getDetails.currentDivisionTitle;
        }
      }
      const rank = option.rules.bonus[key];
      if (rank) {
        let rp = rank;
        option.price = parseFloat(parseFloat(rp).toFixed(2));
        option.type = "fixed";
      }
    }
    if (option.type === "custom") {
      option.price = 0;
      option.type = "free";
    }
  });
  return gamePackOptions;
});

const divineOrbSelected = computed(() => {
  const extraDivOp = getGamePack.pack_options?.find(
    (pack) => pack.const_title === "extra_divine_orbs"
  );
  if (!extraDivOp) return false;
  const isDivineOrb = checkoutStore.getPackOptions.includes(extraDivOp.id);
  return isDivineOrb;
});

const divineOrbsOptions = computed(() => {
  return (
    getGamePack.pack_options?.filter(
      (packOp) =>
        packOp.const_title === "five_divine_orbs" ||
        packOp.const_title === "fifteen_divine_orbs" ||
        packOp.const_title === "thirty_divine_orbs"
    ) || []
  );
});

const switchClick = (option) => {
  // selectOption(option);
};

const renderPrice = (packOption) => {
  if (packOption.type === "free") {
    return packOption.type;
  } else if (packOption.type === "variable") {
    return `+${packOption.price}%`;
  } else if (packOption.type === "fixed") {
    return `+${parseFloat(calculateCurrency(packOption.price)).toFixed(
      2
    )}${handleCurrency(getUserCurrency)}`;
  } else if (packOption.type === "custom" && packOption.price === 0) {
    return "Custom";
  } else {
    return `+${packOption.price}`;
  }
};

const isOptionSelected = (packOption) => {
  const selectedOption = checkoutStore.getPackOptions.find(
    (option) => option === packOption
  );
  if (!selectedOption) {
    return false;
  }
  return true;
};

const selectChildOption = (option) => {
  if (!isOptionSelected(option.id)) {
    checkoutStore.removePackOption({
      optionIds: option?.rules?.disable || [],
    });

    checkoutStore.setPackOptions({
      options: [...checkoutStore.getPackOptions, option.id],
    });
  } else {
  }
};
const selectOption = (option) => {
  if (!isOptionSelected(option.id)) {
    checkoutStore.removePackOption({
      optionIds: option?.rules?.disable || [],
    });
    let assign = [];
    if (option.rules?.assign) {
      assign = [option.rules.assign[0]];
    }

    checkoutStore.setPackOptions({
      options: [...checkoutStore.getPackOptions, option.id, ...assign],
    });
  } else {
    const otherOptions = checkoutStore.getPackOptions.filter(
      (packOpt) => packOpt !== option.id
    );

    checkoutStore.setPackOptions({
      options: [...otherOptions],
    });
  }
};

const isDisabled = (option) => {
  if (option.rules.disable) {
    const isSelected = checkoutStore.getPackOptions?.some((op) =>
      option.rules.disable.includes(op)
    );
    if (isSelected) return true;
    return false;
  }

  return false;
};

watch(divineOrbSelected, (newVal) => {
  const selectedDivineOps = divineOrbsOptions.value.find((op) =>
    checkoutStore.packOptions.includes(op.id)
  );
  if (!newVal && selectedDivineOps) {
    checkoutStore.removePackOption({
      optionIds: [selectedDivineOps.id] || [],
    });
  }
  if (newVal && !selectedDivineOps && divineOrbsOptions.value.length > 0) {
    selectChildOption(divineOrbsOptions.value[0]);
  }
});
</script>

<style lang="scss" scoped>
.divine_orb_image {
  width: 32px;
  height: 32px;
  border-radius: 2.769px;
  border: 0.308px solid var(--bg-main, #050607);
  object-fit: cover;
}
</style>
